import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Mail, 
  Plus, 
  Save, 
  X,
  Building2,
  AlertCircle,
  CheckCircle,
  User,
  UserPlus,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { getPrimaryOUs, getCustomerAdmins, setCustomerAdmins, getAllCustomerAdmins, getUsers, createUser, getOrganizationalUnits } from '../firebase';
import { useUser } from '../contexts/UserContext';
import AccessDenied from '../components/AccessDenied';

const CustomerAdmins = () => {
  const { user, isSuperAdmin } = useUser();
  const [customerAdmins, setCustomerAdminsList] = useState([]);
  const [primaryOUs, setPrimaryOUs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [organisationalUnits, setOrganisationalUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Onboarding form state
  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1); // 1: Select OU, 2: Select/Create User, 3: Add External Email, 4: Review
  const [onboardingData, setOnboardingData] = useState({
    selectedOU: null,
    userSelectionType: 'select', // 'select' or 'create'
    selectedUserEmail: '',
    newUser: {
      firstName: '',
      lastName: '',
      email: '',
      organisationalUnit: ''
    },
    externalEmail: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Check if user is super admin
      const isSuper = isSuperAdmin();
      
      if (!isSuper) {
        setLoading(false);
        return;
      }

      // Super admin: Get all customer admins
      const response = await getAllCustomerAdmins();
      if (response.data?.success) {
        setCustomerAdminsList(response.data.data || []);
      }

      // Get Primary OUs for onboarding
      const primaryOUsResponse = await getPrimaryOUs();
      if (primaryOUsResponse.data?.success) {
        setPrimaryOUs(primaryOUsResponse.data.data || []);
      }

      // Get organisational units for user creation
      const ouResponse = await getOrganizationalUnits();
      if (ouResponse.data?.success) {
        setOrganisationalUnits(ouResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (filterByOU = null) => {
    try {
      setLoadingUsers(true);
      const response = await getUsers();
      if (response.data?.success) {
        let users = response.data.data || [];
        
        // Filter users by selected OU if provided
        if (filterByOU && filterByOU.queuePath) {
          const ouPath = filterByOU.queuePath;
          users = users.filter(user => {
            const userOUPath = user.orgUnitPath || user.organisationalUnit || '/';
            // Include users whose OU path starts with the selected OU path
            // This includes the OU itself and all its children
            return userOUPath === ouPath || userOUPath.startsWith(ouPath + '/');
          });
        }
        
        setAllUsers(users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleStartOnboarding = () => {
    setShowOnboardingForm(true);
    setOnboardingStep(1);
    setOnboardingData({
      selectedOU: null,
      userSelectionType: 'select',
      selectedUserEmail: '',
      newUser: {
        firstName: '',
        lastName: '',
        email: '',
        organisationalUnit: ''
      },
      externalEmail: ''
    });
    setError(null);
    setSuccess(null);
  };

  const handleCancelOnboarding = () => {
    setShowOnboardingForm(false);
    setOnboardingStep(1);
    setOnboardingData({
      selectedOU: null,
      userSelectionType: 'select',
      selectedUserEmail: '',
      newUser: {
        firstName: '',
        lastName: '',
        email: '',
        organisationalUnit: ''
      },
      externalEmail: ''
    });
    setError(null);
    setSuccess(null);
  };

  const handleNextStep = () => {
    // Validate current step before proceeding
    if (onboardingStep === 1) {
      if (!onboardingData.selectedOU) {
        setError('Please select a Primary OU');
        return;
      }
      // Load users filtered by selected OU when moving to step 2 if selecting existing user
      if (onboardingData.userSelectionType === 'select') {
        fetchUsers(onboardingData.selectedOU);
      }
    } else if (onboardingStep === 2) {
      if (onboardingData.userSelectionType === 'select' && !onboardingData.selectedUserEmail) {
        setError('Please select a user');
        return;
      } else if (onboardingData.userSelectionType === 'create') {
        if (!onboardingData.newUser.firstName || !onboardingData.newUser.lastName || !onboardingData.newUser.email) {
          setError('Please fill in all required user fields');
          return;
        }
        if (!onboardingData.newUser.organisationalUnit) {
          setError('Please select an organisational unit for the new user');
          return;
        }
      }
    } else if (onboardingStep === 3) {
      if (!onboardingData.externalEmail) {
        setError('Please enter an external email address');
        return;
      }
      // Validate external email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(onboardingData.externalEmail)) {
        setError('Please enter a valid email address');
        return;
      }
      // Validate it's not from entwinedsignage.com.au
      if (onboardingData.externalEmail.toLowerCase().endsWith('@entwinedsignage.com.au')) {
        setError('External email cannot be from entwinedsignage.com.au domain');
        return;
      }
    }
    
    setError(null);
    setOnboardingStep(onboardingStep + 1);
  };

  const handlePreviousStep = () => {
    setError(null);
    setOnboardingStep(onboardingStep - 1);
  };

  const handleCreateUser = async () => {
    try {
      setLoadingUsers(true);
      setError(null);

      // Find the OU path for the selected OU ID
      const selectedOU = organisationalUnits.find(ou => ou.id === onboardingData.newUser.organisationalUnit);
      const orgUnitPath = selectedOU ? selectedOU.path : '/';

      const response = await createUser({
        firstName: onboardingData.newUser.firstName,
        lastName: onboardingData.newUser.lastName,
        email: onboardingData.newUser.email,
        organisationalUnitId: onboardingData.newUser.organisationalUnit,
        organisationalUnitPath: orgUnitPath,
        password: '', // Let system generate
        alternateEmail: ''
      });

      if (response.data?.success) {
        setSuccess('User created successfully! Proceeding to next step...');
        // Update onboarding data with created user email
        setOnboardingData({
          ...onboardingData,
          selectedUserEmail: onboardingData.newUser.email,
          userSelectionType: 'select' // Switch to select mode so review shows correctly
        });
        // Refresh users list
        await fetchUsers();
        // Move to next step after a brief delay
        setTimeout(() => {
          setOnboardingStep(3);
          setSuccess(null);
        }, 1500);
      } else {
        setError(response.data?.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.message || 'Failed to create user');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveOnboarding = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Get admin user email - either from selection or newly created
      const adminUserEmail = onboardingData.selectedUserEmail || onboardingData.newUser.email;

      const response = await setCustomerAdmins({
        queueId: onboardingData.selectedOU.queueId,
        queueName: onboardingData.selectedOU.queueName,
        queuePath: onboardingData.selectedOU.queuePath,
        adminUserEmail: adminUserEmail,
        externalEmail: onboardingData.externalEmail
      });

      if (response.data?.success) {
        setSuccess('Customer admin onboarding completed successfully');
        await fetchData(); // Refresh the list
        handleCancelOnboarding();
      } else {
        setError(response.data?.message || 'Failed to save customer admin');
      }
    } catch (error) {
      console.error('Error saving customer admin:', error);
      setError(error.message || 'Failed to save customer admin');
    } finally {
      setSaving(false);
    }
  };

  // Check if user has access
  if (!isSuperAdmin() && !loading) {
    return <AccessDenied user={user} reason="You must be a super admin to manage customer admins." />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customer Admin Onboarding</h1>
          <p className="text-gray-600 mt-2">
            Onboard customer administrators for Primary OUs (Queues)
          </p>
        </div>
        {!showOnboardingForm && (
          <button
            onClick={handleStartOnboarding}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Onboard New Admin</span>
          </button>
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Onboarding Form */}
      {showOnboardingForm && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* Progress Steps */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((step) => (
                <React.Fragment key={step}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      onboardingStep >= step 
                        ? 'bg-blue-600 border-blue-600 text-white' 
                        : 'border-gray-300 text-gray-500'
                    }`}>
                      {step}
                    </div>
                    <div className="ml-2 hidden sm:block">
                      <div className={`text-sm font-medium ${
                        onboardingStep >= step ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {step === 1 && 'Select OU'}
                        {step === 2 && 'Select/Create User'}
                        {step === 3 && 'External Email'}
                        {step === 4 && 'Review'}
                      </div>
                    </div>
                  </div>
                  {step < 4 && (
                    <div className={`flex-1 h-0.5 mx-4 ${
                      onboardingStep > step ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step 1: Select Primary OU */}
          {onboardingStep === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Step 1: Select Primary OU</h2>
              <p className="text-gray-600">Choose the Primary OU (Queue) for this customer admin</p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary OU
                </label>
                <select
                  value={onboardingData.selectedOU?.queueId || ''}
                  onChange={(e) => {
                    const selected = primaryOUs.find(ou => ou.queueId === e.target.value);
                    setOnboardingData({ ...onboardingData, selectedOU: selected || null });
                  }}
                  className="input-field"
                  required
                >
                  <option value="">-- Select Primary OU --</option>
                  {primaryOUs.map(ou => (
                    <option key={ou.queueId} value={ou.queueId}>
                      {ou.queueName} ({ou.queuePath})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={handleCancelOnboarding}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNextStep}
                  className="btn-primary flex items-center space-x-2"
                >
                  <span>Next</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Select or Create User */}
          {onboardingStep === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Step 2: Select or Create Admin User</h2>
              <p className="text-gray-600">Choose an existing Google Workspace user or create a new one</p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Selection Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="select"
                      checked={onboardingData.userSelectionType === 'select'}
                      onChange={(e) => {
                        setOnboardingData({ ...onboardingData, userSelectionType: e.target.value, selectedUserEmail: '' });
                        if (e.target.value === 'select' && onboardingData.selectedOU) {
                          fetchUsers(onboardingData.selectedOU);
                        }
                      }}
                      className="mr-2"
                    />
                    <span>Select Existing User</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="create"
                      checked={onboardingData.userSelectionType === 'create'}
                      onChange={(e) => setOnboardingData({ ...onboardingData, userSelectionType: e.target.value })}
                      className="mr-2"
                    />
                    <span>Create New User</span>
                  </label>
                </div>
              </div>

              {onboardingData.userSelectionType === 'select' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select User from {onboardingData.selectedOU?.queueName || 'Selected OU'}
                  </label>
                  {loadingUsers ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-500">Loading users...</p>
                    </div>
                  ) : allUsers.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">No users found in this Primary OU</p>
                      <p className="text-xs text-gray-400 mt-1">You may need to create a new user instead</p>
                    </div>
                  ) : (
                    <select
                      value={onboardingData.selectedUserEmail}
                      onChange={(e) => setOnboardingData({ ...onboardingData, selectedUserEmail: e.target.value })}
                      className="input-field"
                      required
                    >
                      <option value="">-- Select User --</option>
                      {allUsers.map(user => (
                        <option key={user.id} value={user.primaryEmail}>
                          {user.name?.fullName || `${user.name?.givenName} ${user.name?.familyName}`} ({user.primaryEmail})
                          {user.orgUnitPath && user.orgUnitPath !== onboardingData.selectedOU?.queuePath && (
                            <span> - {user.orgUnitPath}</span>
                          )}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {onboardingData.userSelectionType === 'create' && (
                <div className="space-y-4 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900">Create New User</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={onboardingData.newUser.firstName}
                        onChange={(e) => setOnboardingData({
                          ...onboardingData,
                          newUser: { ...onboardingData.newUser, firstName: e.target.value }
                        })}
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={onboardingData.newUser.lastName}
                        onChange={(e) => setOnboardingData({
                          ...onboardingData,
                          newUser: { ...onboardingData.newUser, lastName: e.target.value }
                        })}
                        className="input-field"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={onboardingData.newUser.email}
                      onChange={(e) => setOnboardingData({
                        ...onboardingData,
                        newUser: { ...onboardingData.newUser, email: e.target.value }
                      })}
                      className="input-field"
                      placeholder="user@entwinedsignage.com.au"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organisational Unit *
                    </label>
                    <select
                      value={onboardingData.newUser.organisationalUnit}
                      onChange={(e) => setOnboardingData({
                        ...onboardingData,
                        newUser: { ...onboardingData.newUser, organisationalUnit: e.target.value }
                      })}
                      className="input-field"
                      required
                    >
                      <option value="">-- Select OU --</option>
                      {organisationalUnits.map(ou => (
                        <option key={ou.id} value={ou.id}>
                          {ou.name} ({ou.path})
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={handleCreateUser}
                    disabled={loadingUsers || !onboardingData.newUser.firstName || !onboardingData.newUser.lastName || !onboardingData.newUser.email || !onboardingData.newUser.organisationalUnit}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>{loadingUsers ? 'Creating...' : 'Create User'}</span>
                  </button>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={handlePreviousStep}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Previous</span>
                </button>
                {onboardingData.userSelectionType === 'select' && (
                  <button
                    onClick={handleNextStep}
                    disabled={!onboardingData.selectedUserEmail}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <span>Next</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Add External Email */}
          {onboardingStep === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Step 3: Add External Email</h2>
              <p className="text-gray-600">Add a non-entwinedsignage.com.au email address for the customer admin contact</p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  External Email Address *
                </label>
                <input
                  type="email"
                  value={onboardingData.externalEmail}
                  onChange={(e) => setOnboardingData({ ...onboardingData, externalEmail: e.target.value })}
                  className="input-field"
                  placeholder="admin@customerdomain.com"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  This email cannot be from entwinedsignage.com.au domain
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={handlePreviousStep}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Previous</span>
                </button>
                <button
                  onClick={handleNextStep}
                  disabled={!onboardingData.externalEmail}
                  className="btn-primary flex items-center space-x-2"
                >
                  <span>Next</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {onboardingStep === 4 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Step 4: Review & Save</h2>
              <p className="text-gray-600">Review the information before saving</p>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div>
                  <span className="font-medium text-gray-700">Primary OU:</span>
                  <p className="text-gray-900">{onboardingData.selectedOU?.queueName} ({onboardingData.selectedOU?.queuePath})</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Admin User:</span>
                  <p className="text-gray-900">
                    {onboardingData.selectedUserEmail || onboardingData.newUser.email}
                    {onboardingData.userSelectionType === 'create' && onboardingData.newUser.firstName && (
                      <span className="text-gray-500"> ({onboardingData.newUser.firstName} {onboardingData.newUser.lastName})</span>
                    )}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">External Email:</span>
                  <p className="text-gray-900">{onboardingData.externalEmail}</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={handlePreviousStep}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Previous</span>
                </button>
                <button
                  onClick={handleSaveOnboarding}
                  disabled={saving}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{saving ? 'Saving...' : 'Save & Complete'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Existing Customer Admins List */}
      {!showOnboardingForm && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Shield className="h-6 w-6 text-blue-600 mr-2" />
              Existing Customer Admin Configurations
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {customerAdmins.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Shield className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p>No customer admin configurations found</p>
                <p className="text-sm mt-1">Click "Onboard New Admin" to get started</p>
              </div>
            ) : (
              customerAdmins.map((admin) => (
                <div key={admin.id || admin.queueId} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Building2 className="h-5 w-5 text-gray-400" />
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {admin.queueName || admin.queueId || 'Unknown Queue'}
                          </h3>
                          {admin.queuePath && (
                            <p className="text-sm text-gray-500">Path: {admin.queuePath}</p>
                          )}
                          <p className="text-sm text-gray-500">Queue ID: {admin.queueId}</p>
                        </div>
                      </div>

                      <div className="mt-4 space-y-2">
                        {admin.adminUserEmail && (
                          <div className="flex items-center space-x-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
                            <User className="h-4 w-4 text-gray-400" />
                            <span><strong>Admin User:</strong> {admin.adminUserEmail}</span>
                          </div>
                        )}
                        {admin.externalEmail && (
                          <div className="flex items-center space-x-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span><strong>External Email:</strong> {admin.externalEmail}</span>
                          </div>
                        )}
                        {admin.adminEmails && admin.adminEmails.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">All Admin Emails:</p>
                            {admin.adminEmails.map((email, index) => (
                              <div
                                key={index}
                                className="flex items-center space-x-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md mt-1"
                              >
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span>{email}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAdmins;
